import type { ProgressRequest } from './apiContracts';
import type { PrototypeRuntime } from './prototypeTypes';

export type TrackedChapterProgress = ProgressRequest & {
	chapterIndex: number;
};

/** A navigation can be shown before `getChapter()` returns its opaque id. */
export type ChapterProgressOverlay = Omit<TrackedChapterProgress, 'chapterId'> & {
	chapterId?: string;
};

export interface ChapterProgressIntent {
	topicId: string;
	chapterIndex: number;
	order: number;
	viewerScope: string;
}

export interface ChapterProgressRollback {
	rolledBack: boolean;
	confirmed: TrackedChapterProgress | null;
}

interface OrderedProgress<T> {
	entry: T;
	order: number;
}

interface TopicProgressState {
	viewerScope: string;
	nextOrder: number;
	baseline: OrderedProgress<TrackedChapterProgress> | null;
	pendingConfirmed: OrderedProgress<TrackedChapterProgress> | null;
	intent: OrderedProgress<ChapterProgressOverlay> | null;
}

const queues = new WeakMap<PrototypeRuntime, Map<string, Promise<void>>>();
const states = new WeakMap<PrototypeRuntime, Map<string, TopicProgressState>>();

/**
 * Starts the one unresolved navigation bridge for this viewer/topic. A newer
 * intent replaces an older unresolved one; only successful writes become
 * pending state, so a failed Chapter 3 can never revive a failed Chapter 2.
 */
export function beginChapterProgressIntent(runtime: PrototypeRuntime, topicId: string, chapterIndex: number): ChapterProgressIntent {
	const state = stateFor(runtime, topicId);
	const order = ++state.nextOrder;
	const intent: ChapterProgressIntent = { topicId, chapterIndex, order, viewerScope: state.viewerScope };
	state.intent = {
		order,
		entry: { topicId, chapterIndex, status: 'in_progress', progressPercent: 0 },
	};
	return intent;
}

/**
 * Writes real server ids serially. A matching navigation intent remains the
 * visible bridge until this request succeeds; background writes without an
 * intent only become visible after their request succeeds.
 */
export function recordChapterProgress(runtime: PrototypeRuntime, entry: TrackedChapterProgress, intent?: ChapterProgressIntent): Promise<void> {
	const state = stateFor(runtime, entry.topicId);
	const matchingIntent = intentMatches(state, intent, entry.topicId, entry.chapterIndex);
	const order = matchingIntent ? intent!.order : ++state.nextOrder;
	const viewerScope = state.viewerScope;
	const queueByTopic = queues.get(runtime) ?? new Map<string, Promise<void>>();
	queues.set(runtime, queueByTopic);
	const previous = queueByTopic.get(entry.topicId);
	const write = async (): Promise<void> => {
		if (scopeFor(runtime) !== viewerScope) return;
		await runtime.api.updateProgress({
			topicId: entry.topicId,
			chapterId: entry.chapterId,
			status: entry.status,
			progressPercent: entry.progressPercent,
		});
		promotePendingConfirmed(runtime, entry, order, viewerScope, intent);
	};
	const request = previous ? previous.catch(() => undefined).then(write) : write();
	const settled = request.catch(() => undefined);
	queueByTopic.set(entry.topicId, settled);
	void settled.finally(() => {
		if (queueByTopic.get(entry.topicId) === settled) queueByTopic.delete(entry.topicId);
	});
	// Keep a matching intent intact until its owner decides how to recover the
	// visible Topic CTA. The returned rejection still lets that owner roll back
	// the exact token; this observer only prevents an unhandled rejection.
	void request.catch(() => undefined);
	return request;
}

/** Removes only the same still-unresolved navigation intent. */
export function rollbackChapterProgressIntent(runtime: PrototypeRuntime, intent: ChapterProgressIntent): ChapterProgressRollback {
	const state = existingStateFor(runtime, intent.topicId);
	if (!state || state.viewerScope !== intent.viewerScope || scopeFor(runtime) !== intent.viewerScope || !intentMatches(state, intent, intent.topicId, intent.chapterIndex)) {
		return { rolledBack: false, confirmed: state?.baseline?.entry ?? null };
	}
	state.intent = null;
	return { rolledBack: true, confirmed: state.baseline?.entry ?? null };
}

export function latestChapterProgress(runtime: PrototypeRuntime, topicId: string): ChapterProgressOverlay | null {
	const state = existingStateFor(runtime, topicId);
	if (!state || state.viewerScope !== scopeFor(runtime)) return null;
	return state.intent?.entry ?? state.pendingConfirmed?.entry ?? null;
}

/** Returns only server-acknowledged fallback, never a still-pending local write. */
export function baselineChapterProgress(runtime: PrototypeRuntime, topicId: string): TrackedChapterProgress | null {
	const state = existingStateFor(runtime, topicId);
	return state?.viewerScope === scopeFor(runtime) ? state.baseline?.entry ?? null : null;
}

/** Moves a matching successful local write to the authoritative baseline. */
export function acknowledgeChapterProgress(runtime: PrototypeRuntime, topicId: string, chapterId: string, chapterIndex: number): void {
	const state = existingStateFor(runtime, topicId);
	if (!state || state.viewerScope !== scopeFor(runtime)) return;
	const pending = state.pendingConfirmed;
	if (pending?.entry.chapterId === chapterId && pending.entry.chapterIndex === chapterIndex) {
		state.baseline = pending;
		state.pendingConfirmed = null;
		return;
	}
	const order = ++state.nextOrder;
	state.baseline = { order, entry: { topicId, chapterId, chapterIndex, status: 'in_progress', progressPercent: 0 } };
}

function promotePendingConfirmed(
	runtime: PrototypeRuntime,
	entry: TrackedChapterProgress,
	order: number,
	viewerScope: string,
	intent?: ChapterProgressIntent,
): void {
	const state = existingStateFor(runtime, entry.topicId);
	if (!state || state.viewerScope !== viewerScope || scopeFor(runtime) !== viewerScope) return;
	if (!state.pendingConfirmed || order >= state.pendingConfirmed.order) state.pendingConfirmed = { order, entry };
	if (intentMatches(state, intent, entry.topicId, entry.chapterIndex) && intent?.order === order) state.intent = null;
}

function intentMatches(state: TopicProgressState, intent: ChapterProgressIntent | undefined, topicId: string, chapterIndex: number): boolean {
	return Boolean(intent
		&& intent.topicId === topicId
		&& intent.chapterIndex === chapterIndex
		&& intent.viewerScope === state.viewerScope
		&& state.intent?.order === intent.order);
}

function stateFor(runtime: PrototypeRuntime, topicId: string): TopicProgressState {
	const viewerScope = scopeFor(runtime);
	const stateByTopic = states.get(runtime) ?? new Map<string, TopicProgressState>();
	states.set(runtime, stateByTopic);
	const current = stateByTopic.get(topicId);
	if (current?.viewerScope === viewerScope) return current;
	const next: TopicProgressState = { viewerScope, nextOrder: 0, baseline: null, pendingConfirmed: null, intent: null };
	stateByTopic.set(topicId, next);
	return next;
}

function existingStateFor(runtime: PrototypeRuntime, topicId: string): TopicProgressState | null {
	return states.get(runtime)?.get(topicId) ?? null;
}

function scopeFor(runtime: PrototypeRuntime): string {
	return `${runtime.userId ?? ''}|${runtime.sessionHash ?? ''}`;
}
